import { ActionButton } from '../../components/ActionButton';
import { useControllerConnectionStatus } from '../../Hooks/useControllerConnectionStatus';
import { formatPhysicalIOLabel } from '../../Utils/physical_io_label';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSnapshot } from 'valtio';
import { useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import DialogWithEnterAction from '../../components/DialogWithEnterAction';

import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { useTranslation } from 'react-i18next';
import { GenericSelect, MultiIOSelect, MultiIOSelectIndex } from '../../FormWidgets/Select.js';

import { getMultiIOCurrentLabel, getMultiIOStateValue } from '../../FormWidgets/MultiOutLabel';
import { ConnectionStatus } from '../../FormWidgets/SlaveWidgets.js';
import { SettingActions } from '../../components/index.js';
import {
  multiIOHwTypeMappping,
  multiIOHwTypeMapppingShort,
  multiIOMapping,
  slave_capability_mapping
} from '../../Constants/constants';
import { makeMultiOutObject, GetZoneName } from '../../Utils/utils.js';
import { areValveRouteAssignmentsSupported, getMultiIoIndexConfig, getMultiIoIndexCount } from '../../Utils/multi_io_settings';
import { settingControls, writeTempSettings } from '../../Tabs/settingFunctions';
import getStateManager from '../../StateManager.js';
import { useIsMobile } from '../../Hooks/useMedia.js';
import {
  MultiIOTable,
  sortMultiIORows,
  useMultiIOTableSort
} from '../../components/MultiIOView.js';

import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
  slave: {
    display: 'block',
    border: '0.3em solid black',
    margin: '1em',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '1em',
    boxShadow: '0.5em 0.5em 2em 0.1em #00000'
  },

  label: {
    fontWeight: 'normal',
    color: theme.palette.primary.main
  },

  display: {
    display: 'block',
    padding: '1em',
    margin: '1em 0 1em 0',
    backgroundColor: '#1B1B1B',
    color: 'white'
  },

  lcd: {
    display: 'block',
    backgroundColor: 'blue',
    padding: '1em',
    fontFamily: 'Courier New',
    fontStyle: 'normal',
    border: '0.1em solid #B5B5FF'
  },

  sensors_out_ports: {
    display: 'block',
    padding: '1em',
    margin: '1em 0 1em 0'
  },

  block: {
    display: 'block'
  },

  connection: {
    height: '1.1em',
    width: '1.1em',
    borderRadius: '50%',
    display: 'inline-block',
    margin: '1em'
  },

  portSummary: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },

  slaveTable: {
    width: '100%',
    marginTop: theme.spacing(2),
    borderCollapse: 'collapse',
    '& th, & td': {
      boxSizing: 'border-box',
      padding: theme.spacing(1.25, 1.5),
      textAlign: 'left',
      verticalAlign: 'middle'
    },
    '& thead tr': {
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '& thead': {
      fontSize: theme.typography.body1.fontSize,
      fontWeight: theme.typography.fontWeightRegular,
      textTransform: 'capitalize'
    },
    '& thead th': {
      fontWeight: theme.typography.fontWeightRegular
    },
    '& tbody tr': {
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '& tbody td': {
      // Keep the row footprint stable while connection indicators change state.
      height: theme.spacing(7.5)
    },
    '& th:last-child, & td:last-child': {
      width: 56,
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      textAlign: 'center'
    },
    [theme.breakpoints.down('sm')]: {
      '& th, & td': {
        padding: theme.spacing(1, 0.75)
      },
      '& th:last-child, & td:last-child': {
        width: 44
      }
    }
  },

  portCellLabel: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  moreIcon: {
    color: 'grey'
  }
}));

function PortSummaryTable({ controller, slave }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const columns = [
    { key: 'address', label: 'Adress', width: '20%', sortable: false },
    { key: 'abilities', label: 'Abilities', width: '30%', sortable: false },
    { key: 'connected', label: 'Connected', width: '25%', sortable: false },
    { key: 'zone', label: 'Zone', width: '25%', sortable: false }
  ];

  return (
    <div className={classes.portSummary}>
      <MultiIOTable columns={columns} minWidth={0} ariaLabel={t('show_ports')}>
        <TableRow>
          <TableCell>{slave.address.toString()}</TableCell>
          <TableCell>{t(slave_capability_mapping[slave.capabilities[0]])}</TableCell>
          <TableCell>
            <ConnectionStatus
              controllerStatus={controller?.connection?.status}
              slave={slave}
            />
          </TableCell>
          <TableCell>{t(GetZoneName(controller, slave.zone))}</TableCell>
        </TableRow>
      </MultiIOTable>
      <Typography variant="body2" style={{ marginTop: '0.75em', overflowWrap: 'anywhere' }}>
        {t('slave_firmware')}: {slave.firmware_string?.trim() || t('slave_firmware_unknown')}
      </Typography>
    </div>
  );
}

function getPortSortValue(controller, t, io, key) {
  switch (key) {
    case 'type':
      return t(
        multiIOHwTypeMappping[io.type_id] ??
          multiIOHwTypeMappping['default']
      );
    case 'function':
      return t(multiIOMapping[io.func] ?? multiIOMapping['default']);
    case 'index':
      return getMultiIoIndexConfig(controller)[io.func] ? (io.func_index ?? -1) : null;
    case 'zone':
      return t(GetZoneName(controller, io.zone));
    case 'id':
    default:
      return Number(io.id);
  }
}

export function getSlaveSortValue(controller, t, slave, key) {
  switch (key) {
    case 'abilities':
      return t(slave_capability_mapping[slave.capabilities[0]]);
    case 'connected':
      return controller?.connection?.status === 'good' ? (slave.status ?? '') : 'unknown';
    case 'zone':
      return t(GetZoneName(controller, slave.zone));
    case 'address':
    default:
      return Number(slave.address);
  }
}

export function SlaveTableHead({ columns, onSort, sortDirection, sortKey }) {
  return (
    <thead>
      <tr>
        {columns.map((column) => (
          <th aria-label={column.ariaLabel} key={column.key}>
            <TableSortLabel
              active={sortKey === column.key}
              direction={sortKey === column.key ? sortDirection : 'asc'}
              onClick={() => onSort(column.key)}
              style={{ fontWeight: 'normal' }}
            >
              {column.label}
            </TableSortLabel>
          </th>
        ))}
        <th></th>
      </tr>
    </thead>
  );
}

function Slave({
  slave,
  stateManager,
  controller,
  setDialogWithEnterAction,
  multi_ios,
  setMultiIos,
  read_access,
  write_access,
  mobile
}) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const classes = useStyles();

  const [showOutPort, setShowOutport] = useState(false);
  const closeOutPorts = () => {
    setShowOutport(false);
  }

  const [showSensorPort, setShowSensorPort] = useState(false);
  const closeSensorOutports = () => {
    setShowSensorPort(false);
  }

  // Open correct dialog based on controllers capabilities
  const openPorts = (capability) => {
    if ([10, 20].includes(capability)) {
      setShowOutport(true);
    } else if ([30].includes(capability)) {
      setShowSensorPort(true);
    }
  }

  let setSlaveZone = function (zone, ports = []) {
    if (controller.connection?.status !== 'good') return;
    if (controller.access_rights?.multi_io?.read !== true ||
        controller.access_rights?.multi_io?.write !== true) return;
    if (controller.type === 6000) {
      let data = { slave: slave.address, zone: zone };
      if (ports.length > 0) data.ports = ports;
      stateManager.wsManager.sendWSMsg(controller, 'set_slave_zone', data, true);
    } else {
      const updatedSlaves = controller.slaves.map((s) =>
        s.address === slave.address ? { ...s, zone: parseInt(zone) } : s
      );
      stateManager.saveSettings(controller, controller, updatedSlaves, 'slaves');
    }
  };

  return (
    <>
      <ShowOutPortsDialog
        t={t}
        open={showOutPort}
        handleClose={closeOutPorts}
        controller={controller}
        slave={slave}
        multi_ios={multi_ios}
        setMultiIos={setMultiIos}
        read_access={read_access}
        write_access={write_access}
        mobile={mobile}
      />

      <ShowSensorPortsDialog
        t={t}
        open={showSensorPort}
        handleClose={closeSensorOutports}
        controller={controller}
        slave={slave}
        multi_ios={multi_ios}
        setMultiIos={setMultiIos}
        read_access={read_access}
        write_access={write_access}
        mobile={mobile}
      />

      <tr>
        <td>{slave.address.toString()}</td>
        <td>{t(slave_capability_mapping[slave.capabilities[0]])}</td>
        <td>
          <ConnectionStatus
            controllerStatus={controller?.connection?.status}
            slave={slave}
          />
        </td>

        <td>
          <span title={connectionDisabledReason}>
            <GenericSelect
              disabled={connectionStatus !== 'good' || !write_access}
              style={{ fontSize: mobile ? '0.9em' : '1em' }}
              onChange={(evt) => {
                setDialogWithEnterAction({
                  open: true,
                  apply: () => setSlaveZone(evt.target.value),
                  close: () => setDialogWithEnterAction({ open: false, apply: null, close: null })
                });
              }}
              values={Object.values(controller.zones)
                .map((z) => ({ value: z.id, label: z.name }))
                .concat([
                  { value: 0xfe, label: 'global' },
                  { value: 0xff, label: 'undefined' }
                ])}
              value={slave.zone}
            />
          </span>
        </td>

        <td>
          { [10, 20, 30].includes(slave.capabilities[0]) && (
            <Tooltip title={t('show_ports')}>
              <IconButton
                aria-label={t('show_ports')}
                className={classes.moreIcon}
                onClick={() => openPorts(slave.capabilities[0])}
                size="small"
              >
                <MoreHorizIcon />
              </IconButton>
            </Tooltip>
          )}
        </td>
      </tr>
    </>
  );
}

export default function Slaves() {
  let { id } = useParams();
  const classes = useStyles();
  const { t } = useTranslation();

  const mobile = useIsMobile(960);

  const stateManager = getStateManager();
  const controller = stateManager.getControllerById(id);
  const snapshot = useSnapshot(controller);
  const slaves = snapshot.slaves;
  const read_access = snapshot.access_rights?.multi_io?.read === true;
  const write_access = read_access && snapshot.access_rights?.multi_io?.write === true;

  const [confirmDialog, setDialogWithEnterAction] = useState({ open: false, apply: null, close: null });

  // Pro maps can contain live controller rows. Keep shared dialog edits separate
  // from the readback used by final capability validation, also after access returns.
  const [multi_ios, setMultiIos] = useState(() =>
    read_access ? _.cloneDeep(makeMultiOutObject(controller, undefined)) : {}
  ); // use undefined to get ALL multi-ios
  useEffect(() => {
    setMultiIos(read_access ? _.cloneDeep(makeMultiOutObject(controller, undefined)) : {});
  }, [controller, read_access]);
  const {
    key: slaveSortKey,
    direction: slaveSortDirection,
    requestSort: requestSlaveSort
  } = useMultiIOTableSort('address');
  const sortedSlaves = sortMultiIORows(
    slaves,
    slaveSortKey,
    slaveSortDirection,
    (slave, key) => getSlaveSortValue(controller, t, slave, key)
  );
  const columns = [
    { key: 'address', label: 'Address' },
    { key: 'abilities', label: 'Abilities' },
    { key: 'connected', label: mobile ? null : 'Connected', ariaLabel: 'Connected' },
    { key: 'zone', label: 'Zone' }
  ];

  return (
    <>
      <ConfirmPortZoneDialog controller={controller} data={confirmDialog} write_access={write_access} />
      <table className={classes.slaveTable}>
        <SlaveTableHead
          columns={columns}
          onSort={requestSlaveSort}
          sortDirection={slaveSortDirection}
          sortKey={slaveSortKey}
        />
        <tbody>
          {sortedSlaves.map((slave) => (
            <Slave
              key={slave.address}
              slave={slave}
              setDialogWithEnterAction={setDialogWithEnterAction}
              stateManager={stateManager}
              controller={controller}
              multi_ios={read_access ? multi_ios : {}}
              setMultiIos={setMultiIos}
              read_access={read_access}
              write_access={write_access}
              mobile={mobile}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}

function ConfirmPortZoneDialog({ data, write_access, controller }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const apply = () => {
    if (controller.connection?.status !== 'good') return;
    if (write_access) data.apply();
    data.close();
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={connectionStatus !== 'good'}
      open={data.open}
      onEnterAction={apply}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('confirm-port-zone')}</DialogTitle>
      <DialogContent>
        <Typography> {t('confirm-port-zone')}</Typography>
      </DialogContent>
      <DialogActions>
        <ActionButton
          title={
            connectionDisabledReason || (!write_access ? t('disabled_action_no_write_access') : '')
          }
          disabled={connectionStatus !== 'good' || !write_access}
          onClick={apply}
          color="primary"
        >
          {t('auto_save_ok')}
        </ActionButton>
        <Button onClick={() => data.close()} color="primary">
          {t('cancel')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function savePortSettings(controller, multiIos, controls, enqueueSnackbar, t) {
  if (!areValveRouteAssignmentsSupported(
    controller, undefined, multiIos, makeMultiOutObject(controller, undefined)
  )) {
    enqueueSnackbar(t('valve_irrigation_route_unavailable'), { variant: 'error' });
    return false;
  }
  return controls.saveSettings();
}

function ShowOutPortsDialog({
  open,
  handleClose,
  controller,
  slave,
  multi_ios,
  setMultiIos,
  read_access,
  write_access,
  mobile
}) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const controllerSnapshot = useSnapshot(controller);
  const funcIndexConfig = getMultiIoIndexConfig(controllerSnapshot);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const {
    key: sortKey,
    direction: sortDirection,
    requestSort
  } = useMultiIOTableSort('id');
  const ports = Object.values(multi_ios[slave.address] ?? {});
  const confirmedPorts = makeMultiOutObject(controller, undefined)?.[slave.address] ?? {};
  const sortedPorts = sortMultiIORows(
    ports,
    sortKey,
    sortDirection,
    (io, key) => getPortSortValue(controller, t, io, key)
  );

  const saveSelectIndustrial = (slave, port_id, field, event) => {
    if (!write_access) return;
    let value = event.target.value;
    let updated_ios = { ...multi_ios };
    updated_ios[slave][port_id][field] = value;
    setMultiIos(updated_ios);
  };

  const multi_io_controls = settingControls(
    controller,
    controller,
    'multi_io',
    multi_ios,
    setMultiIos
  );

  multi_io_controls.resetSettings = () => {
    setMultiIos(_.cloneDeep(multi_ios));
    delete controller.temp_settings['multi_io'];
    writeTempSettings(controller);
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={handleClose}
      aria-labelledby="form-dialog-title"
      maxWidth="lg"
      fullWidth
      fullScreen={mobile}
    >
      <DialogTitle id="form-dialog-title">{t('show_ports')}</DialogTitle>
      <DialogContent>

        {write_access && <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={multi_io_controls.resetSettings}
          onSave={() =>
            savePortSettings(controller, multi_ios, multi_io_controls, enqueueSnackbar, t)
          }
        />}

        <PortSummaryTable controller={controller} slave={slave} />

        {read_access && <MultiIOTable
          columns={[
            { key: 'id', label: t('ID'), width: mobile ? '16%' : '15%' },
            { key: 'type', label: t('TYPE'), width: mobile ? '17%' : '22%' },
            { key: 'function', label: t('FUNCTION'), width: mobile ? '30%' : '32%' },
            { key: 'index', label: t('INDEX'), width: mobile ? '15%' : '12%' },
            { key: 'zone', label: t('ZONE'), width: mobile ? '22%' : '19%' }
          ]}
          minWidth={mobile ? 0 : 760}
          ariaLabel={t('show_ports')}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={requestSort}
        >
              {sortedPorts.map((io) => (
                <TableRow key={`${slave.address}-${io.id}`}>
                  <TableCell>
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={3000}
                      title={`IO ${formatPhysicalIOLabel(io.id, slave.dev_variant)}${io.memo !== '' ? ` (${io.memo})` : ''}`}
                    >
                      <label className={classes.portCellLabel}>
                        IO {formatPhysicalIOLabel(io.id, slave.dev_variant)}
                        {!mobile && io.memo !== '' && ` (${io.memo})`}
                      </label>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={3000}
                      title={t(multiIOHwTypeMappping[io.type_id])}
                    >
                      <label className={`${classes.label} ${classes.portCellLabel}`}>
                        {mobile
                          ? multiIOHwTypeMapppingShort[io.type_id]
                          : t(multiIOHwTypeMappping[io.type_id])}
                      </label>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <MultiIOSelect
                      style={{ fontSize: mobile ? '0.9em' : '1em' }}
                      parent={controller.zones[io.zone]}
                      controller={controller}
                      value={io.func ?? ''}
                      controller_value={confirmedPorts[io.id]?.func}
                      type={io.type_id ?? ''}
                      onChange={(event) =>
                        saveSelectIndustrial(slave.address, io.id, 'func', event)
                      }
                      disabled={!write_access}
                    />
                    <div>
                      {getMultiIOCurrentLabel(
                        confirmedPorts[io.id]?.info,
                        confirmedPorts[io.id]?.info_type ?? confirmedPorts[io.id]?.type,
                        t,
                        confirmedPorts[io.id]?.monitor
                      )}
                    </div>
                    {confirmedPorts[io.id]?.state !== undefined && (
                      <div>
                        {getMultiIOStateValue(controller, io.func, confirmedPorts[io.id].state, io.type_id, t)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {funcIndexConfig[io.func] ? (
                      <MultiIOSelectIndex
                        style={{ fontSize: mobile ? '0.9em' : '1em' }}
                        smaller={mobile}
                        count={getMultiIoIndexCount(controllerSnapshot, controller.zones[io.zone], io.func)}
                        value={io.func_index ?? 0}
                        controller_value={confirmedPorts[io.id]?.func_index}
                        onChange={(event) =>
                          saveSelectIndustrial(slave.address, io.id, 'func_index', event)
                        }
                        disabled={!write_access}
                        {...(funcIndexConfig[io.func].mapping && {
                          mapping: typeof funcIndexConfig[io.func].mapping === 'function' ? funcIndexConfig[io.func].mapping(controller, io.zone) : funcIndexConfig[io.func].mapping
                        })}
                      />
                    ) : (
                      'n/a'
                    )}
                  </TableCell>
                  <TableCell>
                    <GenericSelect
                      disabled={!write_access}
                      style={{ fontSize: mobile ? '0.9em' : '1em' }}
                      onChange={(event) =>
                        saveSelectIndustrial(slave.address, io.id, 'zone', event)
                      }
                      values={Object.values(controller.zones)
                        .map((z) => ({ value: z.id, label: z.name }))
                        .concat([
                          { value: String(0xfe), label: 'global' },
                          { value: String(0xff), label: 'undefined' }
                        ])}
                      value={io.zone}
                      controller_value={confirmedPorts[io.id]?.zone}
                    />
                  </TableCell>
                </TableRow>
              ))}
        </MultiIOTable>}

        {write_access && <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={multi_io_controls.resetSettings}
          onSave={() =>
            savePortSettings(controller, multi_ios, multi_io_controls, enqueueSnackbar, t)
          }
        />}

      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('close')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function ShowSensorPortsDialog({
  open,
  handleClose,
  controller,
  slave,
  multi_ios,
  setMultiIos,
  read_access,
  write_access,
  mobile
}) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    key: sortKey,
    direction: sortDirection,
    requestSort
  } = useMultiIOTableSort('id');
  const ports = Object.values(multi_ios[slave.address] ?? {});
  const confirmedPorts = makeMultiOutObject(controller, undefined)?.[slave.address] ?? {};
  const sortedPorts = sortMultiIORows(
    ports,
    sortKey,
    sortDirection,
    (io, key) => getPortSortValue(controller, t, io, key)
  );

  const saveSelectIndustrial = (slave, port_id, field, event) => {
    if (!write_access) return;
    let value = event.target.value;
    let updated_ios = { ...multi_ios };
    updated_ios[slave][port_id][field] = value;
    setMultiIos(updated_ios);
  };

  const multi_io_controls = settingControls(
    controller,
    controller,
    'multi_io',
    multi_ios,
    setMultiIos
  );

  multi_io_controls.resetSettings = () => {
    setMultiIos(_.cloneDeep(multi_ios));
    delete controller.temp_settings['multi_io'];
    writeTempSettings(controller);
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={handleClose}
      aria-labelledby="form-dialog-title"
      maxWidth="lg"
      fullWidth
      fullScreen={mobile}
    >
      <DialogTitle id="form-dialog-title">{t('show_ports')}</DialogTitle>
      <DialogContent>

        {write_access && <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={multi_io_controls.resetSettings}
          onSave={() =>
            savePortSettings(controller, multi_ios, multi_io_controls, enqueueSnackbar, t)
          }
        />}

        <PortSummaryTable controller={controller} slave={slave} />

        {read_access && <MultiIOTable
          columns={[
            { key: 'id', label: t('ID'), width: '55%' },
            { key: 'zone', label: t('ZONE'), width: '45%' }
          ]}
          minWidth={0}
          ariaLabel={t('show_ports')}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={requestSort}
        >
          {sortedPorts.map((io) => (
            <TableRow key={`${slave.address}-${io.id}`}>
              <TableCell>
                Sensor {io.id + 1} {io.memo !== '' && `(${io.memo})`}
              </TableCell>
              <TableCell>
                <GenericSelect
                  disabled={!write_access}
                  style={{ fontSize: mobile ? '0.9em' : '1em' }}
                  onChange={(event) =>
                    saveSelectIndustrial(slave.address, io.id, 'zone', event)
                  }
                  values={Object.values(controller.zones)
                    .map((z) => ({ value: z.id, label: z.name }))
                    .concat([
                      { value: String(0xfe), label: 'global' },
                      { value: String(0xff), label: 'undefined' }
                    ])}
                  value={io.zone}
                  controller_value={confirmedPorts[io.id]?.zone}
                />
              </TableCell>
            </TableRow>
          ))}
        </MultiIOTable>}

        {write_access && <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={multi_io_controls.resetSettings}
          onSave={() =>
            savePortSettings(controller, multi_ios, multi_io_controls, enqueueSnackbar, t)
          }
        />}

      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('close')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
